/**
 * Onboarding Management Dialog Component
 * Full-screen modal for managing user onboarding process
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  X, 
  User, 
  Building, 
  CreditCard, 
  Globe, 
  Printer, 
  Laptop, 
  MapPin, 
  Truck,
  CheckCircle,
  AlertCircle,
  Clock,
  FileText
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useSuperAdminOnboarding } from '@/hooks/useSuperAdminOnboarding';
import { 
  SuperAdminOnboardingData,
  getStatusBadgeVariant,
  getStatusDisplayName,
  calculateOnboardingProgress,
  ONBOARDING_TABS
} from '@/types/super-admin-onboarding';
import { OnboardingStatus } from '@/types/onboarding';
import { getRoleDisplayName, getRoleBadgeVariant } from '@/utils/role-utils';

// Import tab components
import InitialSetupTab from './tabs/InitialSetupTab';
import BusinessDetailsTab from './tabs/BusinessDetailsTab';
import StripeConnectTab from './tabs/StripeConnectTab';
import DomainSetupTab from './tabs/DomainSetupTab';
import PrinterSetupTab from './tabs/PrinterSetupTab';
import WebsiteIntegrationTab from './tabs/WebsiteIntegrationTab';
import GoogleBusinessTab from './tabs/GoogleBusinessTab';
import AggregatorsTab from './tabs/AggregatorsTab';
import RestaurantSettingsTab from './tabs/RestaurantSettingsTab';
import CompleteOnboardingTab from './tabs/CompleteOnboardingTab';

interface OnboardingManagementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  onClose: () => void;
}

const OnboardingManagementDialog: React.FC<OnboardingManagementDialogProps> = ({
  open,
  onOpenChange,
  userId,
  onClose
}) => {
  const [activeTab, setActiveTab] = useState('initial_setup');
  const [onboardingData, setOnboardingData] = useState<SuperAdminOnboardingData | null>(null);
  const [loading, setLoading] = useState(true);

  const { 
    fetchOnboardingDetail, 
    updateOnboardingField, 
    updateOnboardingStatus,
    createRestaurant 
  } = useSuperAdminOnboarding();

  // Load onboarding data when dialog opens
  useEffect(() => {
    if (open && userId) {
      loadOnboardingData();
    }
  }, [open, userId]);

  const loadOnboardingData = async () => {
    try {
      setLoading(true);
      const data = await fetchOnboardingDetail(userId);
      if (data) {
        setOnboardingData(data);
      } else {
        toast({
          title: "Error",
          description: "Could not load onboarding data",
          variant: "destructive"
        });
        onClose();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load onboarding data",
        variant: "destructive"
      });
      onClose();
    } finally {
      setLoading(false);
    }
  };

  // Handle field updates
  const handleFieldUpdate = async (field: keyof SuperAdminOnboardingData, value: any) => {
    if (!onboardingData) return;

    try {
      await updateOnboardingField({
        user_id: userId,
        field,
        value
      });

      // Update local state
      setOnboardingData(prev => prev ? { ...prev, [field]: value } : null);

    } catch (error) {
      // Error is handled in the hook
    }
  };

  // Handle bulk field updates
  const handleBulkFieldUpdate = async (updates: Record<string, any>) => {
    if (!onboardingData) return;

    try {
      // Get only the changed fields by comparing with current data
      const changedFields = Object.entries(updates).filter(([field, value]) => {
        const originalValue = onboardingData[field as keyof SuperAdminOnboardingData];
        return originalValue !== value;
      });

      if (changedFields.length === 0) {
        return { success: true, changedCount: 0 };
      }

      // Update only the changed fields
      const updatePromises = changedFields.map(([field, value]) => 
        updateOnboardingField({
          user_id: userId,
          field: field as keyof SuperAdminOnboardingData,
          value
        })
      );
      
      await Promise.all(updatePromises);

      // Update local state for all changed fields
      setOnboardingData(prev => prev ? { ...prev, ...Object.fromEntries(changedFields) } : null);

      return { success: true, changedCount: changedFields.length };
    } catch (error) {
      throw error;
    }
  };

  // Handle status updates
  const handleStatusUpdate = async (status: OnboardingStatus) => {
    try {
      await updateOnboardingStatus(userId, status);
      
      // Reload data to get updated progress
      await loadOnboardingData();
    } catch (error) {
      // Error is handled in the hook
    }
  };

  // Handle restaurant creation
  const handleCreateRestaurant = async () => {
    try {
      await createRestaurant(userId);
      
      // Reload data to see updated status
      await loadOnboardingData();
      
      // Show success and close dialog
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      // Error is handled in the hook
    }
  };

  // Get tab icon based on tab id
  const getTabIcon = (tabId: string) => {
    const iconMap = {
      initial_setup: User,
      business_details: Building,
      stripe_connect: CreditCard,
      domain_setup: Globe,
      printer_setup: Printer,
      website_integration: Laptop,
      google_business: MapPin,
      aggregators: Truck,
      restaurant_settings: Building,
      complete_onboarding: CheckCircle
    };
    return iconMap[tabId as keyof typeof iconMap] || FileText;
  };

  // Get tab status
  const getTabStatus = (tabId: string): OnboardingStatus => {
    if (!onboardingData) return OnboardingStatus.NOT_STARTED;

    const statusMap = {
      business_details: onboardingData.business_details_status,
      stripe_connect: onboardingData.stripe_connection_status,
      printer_setup: onboardingData.printer_setup_status,
      domain_setup: onboardingData.dns_status,
      website_integration: onboardingData.website_linked_status,
      google_business: onboardingData.google_ordering_status,
      aggregators: onboardingData.uber_aggregation_status || onboardingData.doordash_aggregation_status
    };

    return statusMap[tabId as keyof typeof statusMap] || OnboardingStatus.NOT_STARTED;
  };

  // Get status dot color
  const getStatusDotColor = (status?: OnboardingStatus) => {
    switch (status) {
      case OnboardingStatus.COMPLETE:
        return 'bg-green-500';
      case OnboardingStatus.AWAITING_CONFIRMATION:
        return 'bg-yellow-500';
      case OnboardingStatus.NEEDS_ATTENTION:
        return 'bg-red-500';
      default:
        return 'bg-gray-300';
    }
  };

  if (!onboardingData && !loading) {
    return null;
  }

  const progress = onboardingData ? calculateOnboardingProgress(onboardingData) : { completedStages: 0, totalStages: 8, progressPercentage: 0 };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content className="fixed left-[50%] top-[50%] z-50 flex flex-col w-full max-w-[100vw] sm:max-w-[95vw] max-h-[100vh] sm:max-h-[95vh] translate-x-[-50%] translate-y-[-50%] border bg-background p-0 sm:p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg overflow-hidden">
        <DialogHeader className="flex-shrink-0 px-4 pt-4 sm:px-0 sm:pt-0 pb-2 sm:pb-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div>
                <DialogTitle className="text-lg sm:text-xl">
                  Onboarding Management
                </DialogTitle>
                <DialogDescription className="hidden sm:block">
                  Manage and complete the onboarding process for this user
                </DialogDescription>
                {onboardingData && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mt-2">
                    <div className="text-xs sm:text-sm text-muted-foreground">
                      <strong>{onboardingData.user_name}</strong>
                      <span className="hidden sm:inline"> ({onboardingData.user_email})</span>
                    </div>
                    <Badge variant={getRoleBadgeVariant(onboardingData.user_role)} className="w-fit">
                      {getRoleDisplayName(onboardingData.user_role)}
                    </Badge>
                    {onboardingData.organisation_name && (
                      <div className="text-xs sm:text-sm text-muted-foreground hidden lg:block">
                        {onboardingData.organisation_name}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} className="ml-2">
              <X className="h-4 w-4" />
            </Button>
          </div>

          {onboardingData && (
            <div className="mt-3 sm:mt-4">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1 sm:mb-2">
                    <span className="text-xs sm:text-sm font-medium">Overall Progress</span>
                    <span className="text-xs sm:text-sm text-muted-foreground">
                      {progress.completedStages}/{progress.totalStages} stages
                      <span className="hidden sm:inline"> complete</span>
                    </span>
                  </div>
                  <Progress value={progress.progressPercentage} className="h-1.5 sm:h-2" />
                </div>
                <Badge variant={getStatusBadgeVariant(onboardingData.onboarding_status)} className="hidden sm:inline-flex">
                  {getStatusDisplayName(onboardingData.onboarding_status)}
                </Badge>
              </div>
            </div>
          )}
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading onboarding data...</p>
            </div>
          </div>
        ) : onboardingData ? (
          <div className="flex-1 overflow-y-auto scrollbar-hide px-4 sm:px-0">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <div className="pb-2 lg:pb-4">
                {/* Mobile and landscape mobile: Wrapped grid tabs */}
                <div className="block lg:hidden relative">
                  <TabsList className="grid grid-cols-2 gap-1.5 sm:flex sm:flex-wrap h-auto w-full p-1">
                    {ONBOARDING_TABS.map(tab => {
                      const Icon = getTabIcon(tab.id);
                      const status = getTabStatus(tab.id);

                      return (
                        <TabsTrigger
                          key={tab.id}
                          value={tab.id}
                          className="flex items-center gap-1 px-2 py-1.5 data-[state=active]:!bg-brand-blue data-[state=active]:!text-white data-[state=active]:shadow-sm text-[10px] sm:text-xs min-w-fit sm:flex-[1_0_17%]"
                        >
                          <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${getStatusDotColor(status)}`} />
                          <Icon className={`h-3 w-3 flex-shrink-0 ${activeTab === tab.id ? 'text-white stroke-white' : ''}`} />
                          <span className={`${activeTab === tab.id ? '!text-white' : ''}`}>{tab.title}</span>
                        </TabsTrigger>
                      );
                    })}
                  </TabsList>
                </div>

                {/* Desktop: Grid layout */}
                <div className="hidden lg:block">
                  <TabsList className="grid grid-cols-5 gap-1 w-full min-h-[2.5rem] h-auto">
                    {ONBOARDING_TABS.map(tab => {
                      const Icon = getTabIcon(tab.id);
                      const status = getTabStatus(tab.id);

                      return (
                        <TabsTrigger
                          key={tab.id}
                          value={tab.id}
                          className="flex items-center justify-start gap-2 px-2 py-2 w-full data-[state=active]:!bg-brand-blue data-[state=active]:!text-white data-[state=active]:shadow-sm whitespace-nowrap text-xs"
                        >
                          <div className={`w-3 h-3 rounded-full ${getStatusDotColor(status)}`} />
                          <Icon className={`h-4 w-4 ${activeTab === tab.id ? 'text-white stroke-white' : ''}`} />
                          <span className={`xl:inline ${activeTab === tab.id ? '!text-white' : ''}`}>{tab.title}</span>
                        </TabsTrigger>
                      );
                    })}
                  </TabsList>
                </div>
              </div>

              <div>
                {ONBOARDING_TABS.map((tab) => (
                  <TabsContent
                    key={tab.id}
                    value={tab.id}
                    className="data-[state=inactive]:hidden"
                  >
                    <div className="space-y-4">
                      <div className="pb-4 border-b">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-lg font-semibold">{tab.title}</h3>
                            <p className="text-sm text-muted-foreground">{tab.description}</p>
                          </div>
                        </div>
                      </div>
                      <div className="pb-8">
                        {tab.id === 'initial_setup' && (
                          <InitialSetupTab
                            data={onboardingData}
                            onFieldUpdate={handleFieldUpdate}
                            onBulkFieldUpdate={handleBulkFieldUpdate}
                            onStatusUpdate={handleStatusUpdate}
                          />
                        )}
                        {tab.id === 'business_details' && (
                          <BusinessDetailsTab
                            data={onboardingData}
                            onFieldUpdate={handleFieldUpdate}
                            onBulkFieldUpdate={handleBulkFieldUpdate}
                            onStatusUpdate={handleStatusUpdate}
                          />
                        )}
                        {tab.id === 'stripe_connect' && (
                          <StripeConnectTab
                            data={onboardingData}
                            onFieldUpdate={handleFieldUpdate}
                            onBulkFieldUpdate={handleBulkFieldUpdate}
                            onStatusUpdate={handleStatusUpdate}
                          />
                        )}
                        {tab.id === 'domain_setup' && (
                          <DomainSetupTab
                            data={onboardingData}
                            onFieldUpdate={handleFieldUpdate}
                            onBulkFieldUpdate={handleBulkFieldUpdate}
                            onStatusUpdate={handleStatusUpdate}
                          />
                        )}
                        {tab.id === 'printer_setup' && (
                          <PrinterSetupTab
                            data={onboardingData}
                            onFieldUpdate={handleFieldUpdate}
                            onBulkFieldUpdate={handleBulkFieldUpdate}
                            onStatusUpdate={handleStatusUpdate}
                          />
                        )}
                        {tab.id === 'website_integration' && (
                          <WebsiteIntegrationTab
                            data={onboardingData}
                            onFieldUpdate={handleFieldUpdate}
                            onBulkFieldUpdate={handleBulkFieldUpdate}
                            onStatusUpdate={handleStatusUpdate}
                          />
                        )}
                        {tab.id === 'google_business' && (
                          <GoogleBusinessTab
                            data={onboardingData}
                            onFieldUpdate={handleFieldUpdate}
                            onBulkFieldUpdate={handleBulkFieldUpdate}
                            onStatusUpdate={handleStatusUpdate}
                          />
                        )}
                        {tab.id === 'aggregators' && (
                          <AggregatorsTab
                            data={onboardingData}
                            onFieldUpdate={handleFieldUpdate}
                            onBulkFieldUpdate={handleBulkFieldUpdate}
                            onStatusUpdate={handleStatusUpdate}
                          />
                        )}
                        {tab.id === 'restaurant_settings' && (
                          <RestaurantSettingsTab
                            data={onboardingData}
                            onFieldUpdate={handleFieldUpdate}
                            onBulkFieldUpdate={handleBulkFieldUpdate}
                            onStatusUpdate={handleStatusUpdate}
                          />
                        )}
                        {tab.id === 'complete_onboarding' && (
                          <CompleteOnboardingTab
                            data={onboardingData}
                            onCreateRestaurant={handleCreateRestaurant}
                            onStatusUpdate={handleStatusUpdate}
                          />
                        )}
                      </div>
                    </div>
                  </TabsContent>
                ))}
              </div>
            </Tabs>
          </div>
        ) : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </Dialog>
  );
};

export default OnboardingManagementDialog;